import type { Move } from '../../../schemas/move';

/**
 * Mosse avanzate del Ranger L2-5 (manuale V1, rr. 5260-5354). 16 mosse.
 *
 * Pattern notabili:
 * - **2 mosse richiedono *Marchio del Cacciatore*** (Mostrami il Fianco,
 *   Cacciare in Branco).
 * - **2 mosse richiedono *Compagno Animale*** (Ben Addestrato, Il Miglior
 *   Amico dell'Uomo) — FI-8 stub: prerequisito strutturato anche se il
 *   Compagno è description-only.
 * - **Mossa multiclasse**: *Abilità Secondarie* — sceglie da Barbaro,
 *   Druido, Guerriero, Ladro o Sciamano. Description-only (FI-17).
 * - ***Vecchi Amici***: 3 opzioni di gregari (bibliotecario/mercenario/
 *   taverniere). FI-8 stub description-only.
 * - ***Riserva di Caccia***: pseudo-gregario (prese di "trappole/attacchi
 *   pre-piazzati"). FI-8 al limite, description-only.
 *
 * **Nessuna catena chiusa qui**: le catene L2-5 → L6-10 sono in
 * advanced-6-10.ts (Preda Familiare → La Preda del Cacciatore; Bersaglio
 * Facile → Tiro Perfetto).
 */

const rangerATuttoTondo: Move = {
  id: 'ranger.ranger-a-tutto-tondo',
  name: 'Ranger a Tutto Tondo',
  type: 'advanced_2_5',
  classId: 'ranger',
  trigger: 'Quando prendi questa mossa al level-up',
  description: 'Se hai già preso *Compagno Animale* prendi *Marchio del Cacciatore*, e viceversa.',
  tags: [],
  prerequisites: [],
  replaces: [],
  // FI-31 (D5): concede la mossa iniziale rimanente del gruppo del Ranger
  // (Marchio del Cacciatore / Compagno Animale — pool=1 residuo).
  grantsMoveChoice: { kind: 'from-group', groupId: 'mossa-iniziale-aggiuntiva' },
};

const mostramiIlFianco: Move = {
  id: 'ranger.mostrami-il-fianco',
  name: 'Mostrami il Fianco',
  type: 'advanced_2_5',
  classId: 'ranger',
  trigger: 'Quando infliggi danno a una creatura marchiata con *Marchio del Cacciatore*',
  description: "Applica l'etichetta +3 *perforante*.",
  tags: [],
  prerequisites: ['ranger.marchio-del-cacciatore'],
  replaces: [],
};

const cacciareInBranco: Move = {
  id: 'ranger.cacciare-in-branco',
  name: 'Cacciare in Branco',
  type: 'advanced_2_5',
  classId: 'ranger',
  trigger: 'Quando rimuovi il *Marchio del Cacciatore* da una creatura',
  description:
    "Se crei un vantaggio, anche tutti i tuoi alleati che lo sfruttano prendono +1 al prossimo tiro. Se scegli di trasformare in 10+ un tiro di *sfidare il pericolo*, invece, puoi decidere se applicare l'effetto a te stesso o a un altro personaggio.",
  tags: [],
  prerequisites: ['ranger.marchio-del-cacciatore'],
  replaces: [],
};

const benAddestrato: Move = {
  id: 'ranger.ben-addestrato',
  name: 'Ben Addestrato',
  type: 'advanced_2_5',
  classId: 'ranger',
  trigger: 'Quando prendi questa mossa al level-up (upgrade del Compagno Animale)',
  description:
    'Scegli altre due capacità utili dalla lista per il tuo *Compagno Animale*. Concordale con il GM (label + descrizione).',
  tags: [],
  prerequisites: ['ranger.compagno-animale'],
  replaces: [],
  // D8c — FI-41: upgrade del Compagno Animale, 2 capacità custom sempre
  // presenti dichiarate via narrativeFields. Il template
  // `ranger-compagno-animale` dichiara l'upgrade con
  // `additionalAlwaysTraitsFromNarrativeFields` che mappa qui.
  narrativeFields: [
    {
      id: 'ba-cap1-label',
      label: '1ª capacità utile — titolo',
      placeholder: 'es. Tracciatore esperto, Vede al buio, Sentinella…',
      required: true,
      multiline: false,
    },
    {
      id: 'ba-cap1-desc',
      label: '1ª capacità utile — descrizione',
      placeholder: 'Effetto narrativo o meccanico, concordato con il GM.',
      required: false,
      multiline: true,
    },
    {
      id: 'ba-cap2-label',
      label: '2ª capacità utile — titolo',
      placeholder: 'es. Pelle squamosa, Volo planato, Combattente esperto…',
      required: true,
      multiline: false,
    },
    {
      id: 'ba-cap2-desc',
      label: '2ª capacità utile — descrizione',
      placeholder: 'Effetto narrativo o meccanico, concordato con il GM.',
      required: false,
      multiline: true,
    },
  ],
};

const ilMigliorAmicoDellUomo: Move = {
  id: 'ranger.il-miglior-amico-dell-uomo',
  name: "Il Miglior Amico dell'Uomo",
  type: 'advanced_2_5',
  classId: 'ranger',
  trigger: 'Quando permetti al tuo *Compagno Animale* di subire un colpo destinato a te',
  description:
    'Il danno è annullato, ma non puoi usare nuovamente questa mossa finché non ti *accampi*.',
  tags: [],
  prerequisites: ['ranger.compagno-animale'],
  replaces: [],
};

const empatiaAnimale: Move = {
  id: 'ranger.empatia-animale',
  name: 'Empatia Animale',
  type: 'advanced_2_5',
  classId: 'ranger',
  trigger: 'Quando prendi questa mossa al level-up',
  description: 'Puoi comprendere ed essere compreso dagli animali.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const predaFamiliare: Move = {
  id: 'ranger.preda-familiare',
  name: 'Preda Familiare',
  type: 'advanced_2_5',
  classId: 'ranger',
  trigger: 'Quando *declami conoscenze* riguardo a un mostro',
  description: 'Puoi decidere di tirare+**SAG** invece di +**INT**.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const piazzareTrappole: Move = {
  id: 'ranger.piazzare-trappole',
  name: 'Piazzare Trappole',
  type: 'advanced_2_5',
  classId: 'ranger',
  trigger: 'Quando dedichi alcuni minuti ad allestire una trappola',
  description:
    'Spiega che effetto vorresti che avesse e quali materiali a tua disposizione intendi utilizzare per realizzarla. Se il GM approva il tuo piano, tira+**DES**. Con un 10+, tutto funziona esattamente come volevi. Con 7-9, la trappola funziona lo stesso, ma ha un effetto collaterale inatteso che scoprirai solo quando si manifesta.',
  roll: {
    stat: 'destrezza',
    on10plus: { text: 'Tutto funziona esattamente come volevi.' },
    on7to9: {
      text: 'La trappola funziona, ma ha un effetto collaterale inatteso (scoperto solo quando si manifesta).',
    },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const mimetizzarsi: Move = {
  id: 'ranger.mimetizzarsi',
  name: 'Mimetizzarsi',
  type: 'advanced_2_5',
  classId: 'ranger',
  trigger: 'Quando resti immobile in un ambiente naturale',
  description: 'I nemici non possono scorgerti finché non ti muovi.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const riservaDiCaccia: Move = {
  id: 'ranger.riserva-di-caccia',
  name: 'Riserva di Caccia',
  type: 'advanced_2_5',
  classId: 'ranger',
  trigger: 'Quando hai avuto il tempo di preparare una zona in anticipo',
  description:
    "Tira+**DES**. Con 10+, prendi 2. Con 7-9, lo stesso, ma anche il GM prende 1. Quando chiunque, tranne te stesso, si muove all'interno dell'area che hai preparato, tu o il GM potete spendere una presa per scegliere una delle seguenti opzioni:\n\n- il bersaglio viene fermato da una trappola che lo blocca momentaneamente sul posto;\n- infliggi il tuo dado di Danno al bersaglio con una trappola o con un attacco a sorpresa (a tua scelta);\n- appari all'improvviso in un qualunque punto all'interno della zona che hai preparato.",
  roll: {
    stat: 'destrezza',
    on10plus: { text: 'Prendi 2.' },
    on7to9: { text: 'Prendi 2, ma anche il GM prende 1.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const maDoveLAvevoMesso: Move = {
  id: 'ranger.ma-dove-l-avevo-messo',
  name: "Ma Dove l'avevo Messo?",
  type: 'advanced_2_5',
  classId: 'ranger',
  trigger:
    'Quando conosci uno specifico tipo di arma, strumento o sostanza che ti può consentire di sconfiggere una preda, e ne hai bisogno',
  description:
    "Se è una cosa comune e di poco prezzo, semplicemente ce l'hai. Se invece è qualcosa di raro o prezioso, tira+**SAG**. Con 10+, hai nello zaino qualcosa di meno raro o prezioso, ma comunque perfetto per lo scopo. Con 7-9, il GM sceglie una delle seguenti opzioni:\n\n- hai qualcosa di simile che funzionerà, ma usarlo comporta dei rischi;\n- non ce l'hai ma hai una chiara idea su dove potertelo procurare.",
  roll: {
    stat: 'saggezza',
    on10plus: { text: 'Hai qualcosa di meno raro/prezioso ma perfetto per lo scopo.' },
    on7to9: { text: 'Il GM sceglie 1 opzione dalla lista.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const bersaglioFacile: Move = {
  id: 'ranger.bersaglio-facile',
  name: 'Bersaglio Facile',
  type: 'advanced_2_5',
  classId: 'ranger',
  trigger: 'Quando fai una *raffica* contro un nemico sorpreso o indifeso',
  description:
    'Puoi scegliere di infliggere il tuo dado di Danno senza bisogno di tirare, oppure puoi tentare di ottenere uno specifico effetto e tirare+**DES**. Con 10+, scegli un effetto dalla lista e infliggi il tuo dado di Danno. Con 7-9, scegli comunque un effetto, ma non infliggi danno:\n\n- il bersaglio rimane temporaneamente stordito;\n- il bersaglio fa cadere qualunque cosa abbia in mano;\n- il bersaglio zoppica e si muove lentamente.',
  roll: {
    stat: 'destrezza',
    on10plus: { text: 'Scegli 1 effetto dalla lista e infliggi il tuo dado di Danno.' },
    on7to9: { text: 'Scegli 1 effetto, ma non infliggi danno.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const cacciatoreDiTaglie: Move = {
  id: 'ranger.cacciatore-di-taglie',
  name: 'Cacciatore di Taglie',
  type: 'advanced_2_5',
  classId: 'ranger',
  trigger: "Quando arrivi in un insediamento civilizzato e dai un'occhiata ai poster dei ricercati",
  description:
    'Tira+**SAG**. Con 10+, vieni a sapere di due minacce locali (criminali o mostri). Con 7-9, solo di una. In ogni caso sai qual è la ricompensa per la loro cattura o eliminazione, e hai +1 continuato a qualunque tiro nei confronti di questi ricercati. Con un 6-, sei tu a essere ricercato, e hai -1 continuato a qualsiasi tiro che fai per interagire pacificamente con le autorità locali.',
  roll: {
    stat: 'saggezza',
    on10plus: { text: 'Vieni a sapere di 2 minacce locali; +1 continuato contro di esse.' },
    on7to9: { text: 'Vieni a sapere di 1 minaccia locale; +1 continuato contro di essa.' },
    on6minus: {
      text: 'Sei tu a essere ricercato; -1 continuato a interazioni pacifiche con le autorità.',
    },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const abilitaSecondarie: Move = {
  id: 'ranger.abilita-secondarie',
  name: 'Abilità Secondarie',
  type: 'advanced_2_5',
  classId: 'ranger',
  trigger: 'Quando prendi questa mossa al level-up',
  description:
    'Scegli una mossa del Barbaro, del Druido, del Guerriero, del Ladro o dello Sciamano. Non scegliere mosse multiclasse.',
  tags: ['multiclasse'],
  prerequisites: [],
  replaces: [],
  // FI-17 (D7): pool ristretto a 5 classi.
  multiclass: {
    picks: {
      kind: 'fixed-classes',
      classes: ['barbaro', 'druido', 'guerriero', 'ladro', 'sciamano'],
    },
  },
};

const oscurareIlSole: Move = {
  id: 'ranger.oscurare-il-sole',
  name: 'Oscurare il Sole',
  type: 'advanced_2_5',
  classId: 'ranger',
  trigger: 'Quando fai una *raffica*',
  description:
    'Puoi decidere di impiegare *munizioni* extra del tipo appropriato alla tua arma prima di tirare. Per ogni *munizione* spesa in anticipo, scegli un bersaglio oltre al primo. Se hai successo, tira per i danni una volta sola e applica il risultato a ogni bersaglio selezionato.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

// FI-8 stub: i 3 tipi di gregari (bibliotecario / mercenario / taverniere) restano
// description-only fino alla PR FI-8 unificata (post-12 classi). FI-18 Fase 1
// struttura solo nome + luogo dei 3 amici come `narrativeFields`.
const vecchiAmici: Move = {
  id: 'ranger.vecchi-amici',
  name: 'Vecchi Amici',
  type: 'advanced_2_5',
  classId: 'ranger',
  trigger: 'Quando prendi questa mossa al level-up',
  description:
    "Hai degli amici che occasionalmente ti forniscono il loro aiuto. Racconta come vi siete conosciuti, se è accaduto qualcosa che vi ha reso alleati, o se semplicemente ti devono un favore. Dai a ciascuno di loro un nome e definisci il luogo in cui puoi trovarli quando ne hai bisogno:\n\n- *bibliotecario*: un esperto di ricerca su cui puoi contare per scoprire informazioni riguardo qualsiasi argomento. A volte ha bisogno di un favore, o di tempo, ma avrà sempre informazioni utili;\n- *mercenario*: un compagno su cui puoi contare per aiutarti nella caccia. Trattalo come un gregario di livello 2. Ha Lealtà 2, +1 a tutti i modificatori di Caratteristica, 10 PF, Carico 6 e d6 come dado di Danno. Il mercenario ha anche un'Abilità a Rango 1 a tua scelta tra Guardia del Corpo e Combattente. In genere ti fornisce il suo aiuto senza alcun Costo, ma se chiedi troppo di frequente i suoi servigi potrebbe domandarti un favore in cambio;\n- *taverniere*: conosce tutti quelli che vale la pena conoscere. Cerchi qualcuno o qualcosa? Lui ti indirizzerà verso la persona giusta. A volte potrebbe volere qualcosa in cambio, soprattutto se richiedi spesso il suo aiuto.",
  tags: [],
  prerequisites: [],
  replaces: [],
  // FI-18: intersezione FI-8 — 3 amici, 2 campi per ognuno (nome + luogo).
  narrativeFields: [
    {
      id: 'nome-bibliotecario',
      label: 'Nome del bibliotecario',
      placeholder: 'es. Maestro Talheim',
      required: true,
      multiline: false,
    },
    {
      id: 'luogo-bibliotecario',
      label: 'Dove trovare il bibliotecario',
      placeholder: 'es. la Biblioteca di Adon, il monastero di Senna…',
      required: false,
      multiline: false,
    },
    {
      id: 'nome-mercenario',
      label: 'Nome del mercenario',
      placeholder: 'es. Roggo Mannara',
      required: true,
      multiline: false,
    },
    {
      id: 'luogo-mercenario',
      label: 'Dove trovare il mercenario',
      placeholder: 'es. la Gilda della Faretra Rossa, la taverna del Lupo…',
      required: false,
      multiline: false,
    },
    {
      id: 'nome-taverniere',
      label: 'Nome del taverniere',
      placeholder: 'es. Geld il Grasso',
      required: true,
      multiline: false,
    },
    {
      id: 'luogo-taverniere',
      label: 'Dove trovare il taverniere',
      placeholder: "es. la Taverna delle Tre Code, l'Osteria del Vecchio Porto…",
      required: false,
      multiline: false,
    },
  ],
};

export const rangerAdvanced2to5Moves: readonly Move[] = [
  rangerATuttoTondo,
  mostramiIlFianco,
  cacciareInBranco,
  benAddestrato,
  ilMigliorAmicoDellUomo,
  empatiaAnimale,
  predaFamiliare,
  piazzareTrappole,
  mimetizzarsi,
  riservaDiCaccia,
  maDoveLAvevoMesso,
  bersaglioFacile,
  cacciatoreDiTaglie,
  abilitaSecondarie,
  oscurareIlSole,
  vecchiAmici,
] as const;
